import React, { useState, useRef, useEffect } from 'react';
import {
    Plus, Trash2, Columns, Printer, MousePointer2,
    Type, Upload, CheckCircle2, RotateCcw, Maximize2
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import html2pdf from "html2pdf.js";
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

const LiveReportPreview = () => {
    const location = useLocation();
    const fileInputRef = useRef(null);

    // --- State Management ---
    const [reportIndications, setReportIndications] = useState(location.state?.indications || [{}]);
    const [reportPartDetails, setReportPartDetails] = useState(location.state?.partDetails || { Project: "", Material: "" });
    const [reportScanParams, setReportScanParams] = useState(location.state?.scanParams || { Equipment: "", Probe: "" });
    const [companyInfo, setCompanyInfo] = useState({
        name: "Company Logo", tagline: "SubTitle", address: "Address", logo: null
    });

    const [fontSize, setFontSize] = useState('10px');
    const [fontWeight, setFontWeight] = useState('700');
    const [highlightColumn, setHighlightColumn] = useState(null);
    const [fontColor, setFontColor] = useState('#1e3a8a'); // default blue-900

    const [fontFamily, setFontFamily] = useState("Arial, sans-serif")


    const [selectedArea, setSelectedArea] = useState('companyName'); // Track the specific field

    const [fieldStyles, setFieldStyles] = useState({
        companyName: { fontSize: '18px', fontWeight: '700', fontColor: '#000000' },
        tagline: { fontSize: '10px', fontWeight: '500', fontColor: '#64748b' },
        reportTitle: { fontSize: '14px', fontWeight: '900', fontColor: '#000000' },
        metaNo: { fontSize: '9px', fontWeight: '500', fontColor: '#000000' },
        metaDate: { fontSize: '9px', fontWeight: '500', fontColor: '#000000' },
        // New keys for the tables and footer
        general: { fontSize: '10px', fontWeight: '700', fontColor: '#000000' },
        part: { fontSize: '10px', fontWeight: '700', fontColor: '#1e40af' }, // Default blue-800
        scan: { fontSize: '10px', fontWeight: '700', fontColor: '#000000' },
        table: { fontSize: '9px', fontWeight: '500', fontColor: '#000000' },
        // footer: { fontSize: '10px', fontWeight: '900', fontColor: '#000000' },
    });

    const [user, setUser] = useState(null);

useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
    setUser(currentUser);
  });
  return () => unsubscribe();
}, []);

    // Helper function to update only the active field's style
    const updateFieldStyle = (property, value) => {
        setFieldStyles(prev => ({
            ...prev,
            [selectedArea]: {
                ...prev[selectedArea],
                [property]: value
            }
        }));
    };

    const [reportGeneralDetails, setReportGeneralDetails] = useState({
        "Project Name": "",
        "Contractor": "",
        "Client Name": "",
        "Procedure No": "",
        "Applicable Code": "",
        "Base Material": "",
        "Surface Condition": "",
        "Joint Type": "",
        "Welding Process": "",
        "Stage of Examination": "",
        "Drawing No": ""
    });

    console.log("reportIndications", reportIndications, "a", reportPartDetails, "b", reportScanParams, "image", location.state?.weldImage)
    // --- UI State ---
    // const [selectedArea, setSelectedArea] = useState(null);
    const [isEditMode, setIsEditMode] = useState(true);
    const [orientation, setOrientation] = useState('portrait');

    // --- Unified Logic for Add/Delete/Edit ---
    const handleAddItem = (e) => {
        if (e) e.stopPropagation(); // Prevents deselection when clicking the tool button
        if (!selectedArea) return alert("Click on a table section first!");

        if (selectedArea === 'table') {
            const newRow = { id: Date.now() };
            Object.keys(reportIndications[0] || {}).forEach(c => { if (c !== 'id') newRow[c] = ""; });
            setReportIndications([...reportIndications, newRow]);
        } else {
            const label = prompt("Enter New Field Label:");
            if (!label) return;
            if (selectedArea === 'general') setReportGeneralDetails({ ...reportGeneralDetails, [label]: "Value" });
            if (selectedArea === 'part') setReportPartDetails({ ...reportPartDetails, [label]: "Value" });
            if (selectedArea === 'scan') setReportScanParams({ ...reportScanParams, [label]: "Value" });
        }
    };

    const handleAddColumn = (e) => {
        if (e) e.stopPropagation(); // Prevents deselection
        if (selectedArea !== 'table') return alert("Columns can only be added to the Main Findings Table.");
        const colCount = Object.keys(reportIndications[0] || {}).length;
        if (colCount > 15) return alert("Maximum 15 columns reached for A4 limit.");

        const colName = prompt("New Column Name:");
        if (colName) setReportIndications(reportIndications.map(row => ({ ...row, [colName]: "" })));
    };

    const handleDeleteLast = (e) => {
        if (e) e.stopPropagation(); // Prevents deselection
        if (!selectedArea) return;
        if (selectedArea === 'table') {
            setReportIndications(reportIndications.slice(0, -1));
        } else if (selectedArea === 'general') {
            const keys = Object.keys(reportGeneralDetails);
            const updated = { ...reportGeneralDetails };
            delete updated[keys[keys.length - 1]];
            setReportGeneralDetails(updated);
        } else if (selectedArea === 'part') {
            const keys = Object.keys(reportPartDetails);
            const updated = { ...reportPartDetails };
            delete updated[keys[keys.length - 1]];
            setReportPartDetails(updated);
        } else if (selectedArea === 'scan') {
            const keys = Object.keys(reportScanParams);
            const updated = { ...reportScanParams };
            delete updated[keys[keys.length - 1]];
            setReportScanParams(updated);
        }
    };

    const getSelectionStyle = (area) =>
        selectedArea === area
            ? "ring-2 ring-blue-600 ring-offset-2 ring-offset-white"
            : "";




    const exportReportPDF = async () => {
        const source = document.querySelector(".report-canvas");
        if (!source) return;

        // 1. Create the clone
        const clone = source.cloneNode(true);

        // 2. Convert Inputs/Textareas to Spans
        const originalInputs = source.querySelectorAll("input, textarea");
        const clonedInputs = clone.querySelectorAll("input, textarea");

        originalInputs.forEach((input, index) => {
            const clonedInput = clonedInputs[index];
            const parent = clonedInput.parentElement;
            const textNode = document.createElement("span");

            textNode.innerText = input.value || " ";
            const style = getComputedStyle(input);

            textNode.style.display = "flex";
            textNode.style.alignItems = "center";
            textNode.style.justifyContent = style.textAlign === 'center' ? 'center' : 'flex-start';

            textNode.style.width = "100%";
            textNode.style.fontSize = style.fontSize;
            textNode.style.fontWeight = style.fontWeight;
            textNode.style.fontFamily = style.fontFamily;
            textNode.style.color = style.color;
            textNode.style.paddingBottom = "2px";
            textNode.style.lineHeight = "1";

            parent.replaceChild(textNode, clonedInput);
        });

        // 3. FORCE TABLE WIDTHS (Crucial for fitting)
        // This ensures no table column expands beyond the page width
        const tables = clone.querySelectorAll("table");
        tables.forEach(table => {
            table.style.width = "100%";
            table.style.tableLayout = "fixed";
        });

        // 4. Create a hidden container with a FIXED PIXEL WIDTH
        const container = document.createElement("div");
        container.style.position = "absolute";
        container.style.left = "-9999px";
        container.style.top = "0";
        // 794px is the equivalent of 210mm at 96 DPI
        container.style.width = "794px";

        // Force the clone to match the A4 pixel width exactly
        clone.style.width = "794px";
        clone.style.background = "white";
        clone.style.margin = "0";
        clone.style.padding = "10mm"; // Matches standard report margins
        clone.style.boxSizing = "border-box";
        clone.style.border = "none";
        clone.style.outline = "none";

        container.appendChild(clone);
        document.body.appendChild(container);

        const options = {
            margin: 0, // We handle margins inside the 'clone' padding
            filename: `NDT-Report-${new Date().getTime()}.pdf`,
            image: { type: "jpeg", quality: 1.0 },
            html2canvas: {
                scale: 3,
                useCORS: true,
                letterRendering: true,
                // windowWidth ensures the virtual "browser" is the same size as A4
                windowWidth: 794,
                scrollY: 0,
                scrollX: 0
            },
            jsPDF: {
                unit: "mm",
                format: "a4",
                orientation: "portrait",
                compress: true
            }
        };

        try {
            // Capture specifically from the 'clone' inside the 'container'
            await html2pdf().from(clone).set(options).save();
        } catch (err) {
            console.error("PDF Export Error:", err);
        } finally {
            document.body.removeChild(container);
        }
    };

    const columnMapping = {
        amplitude_percent: 'Amp %',
        // bbox: 'BBox',
        depth_mm: 'Depth',
        end_mm: 'End',
        height_mm: 'Height',
        length_mm: 'Length',
        pixel_area: 'Area',
        reject_reason: 'Reason',
        start_mm: 'Start',
        status: 'Status',
        width_mm: 'Width',
    };

    const formatValue = (val) => {
        if (val === null || val === undefined) return '-';
        // Check if it's a number or a string that looks like a number
        if (!isNaN(val) && val !== '' && typeof val !== 'boolean') {
            return parseFloat(val).toFixed(2);
        }
        return val;
    };

    return (
        /* UPDATED: Added onClick to container to deselect everything */
        <div className="flex h-screen bg-slate-200 overflow-hidden font-sans" onClick={() => setSelectedArea(null)}>

            {/* --- POWER SIDEBAR --- */}
            <aside className="w-72 bg-slate-900 text-white flex flex-col z-[100] print:hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>

                {/* NAVIGATION LINK */}
              <div className="p-4 border-b border-slate-800">
    <Link
        to={user ? "/engine" : "/"}
        className="flex items-center gap-2 text-slate-400 hover:text-blue-400 transition-all text-[10px] font-black uppercase tracking-widest group"
    >
        <RotateCcw size={14} className="group-hover:-rotate-90 transition-transform duration-300" />
        {/* Dynamic text to match the destination */}
        {user ? "Back to Dashboard" : "Back to Home"}
    </Link>
</div>
                <div className="p-6 border-b border-slate-800 bg-slate-950">
                    <h2 className="text-blue-400 text-xs font-black tracking-widest uppercase">Master Control</h2>
                    <p className="text-[10px] text-slate-500 uppercase">Active: {selectedArea || 'None'}</p>
                </div>

                <div className="flex-1 p-4 space-y-6 overflow-y-auto">
                    {/* Orientation & Global */}
                    <div className="space-y-2">
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">Page Settings</p>
                        <button onClick={() => setOrientation(orientation === 'portrait' ? 'landscape' : 'portrait')} className="w-full flex items-center justify-between px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-xs border border-slate-700">
                            <Maximize2 size={14} /> Rotate to {orientation === 'portrait' ? 'Landscape' : 'Portrait'}
                        </button>
                    </div>

                    {/* Text Styling */}
                    {/* Text Styling Sidebar/Panel */}
<div className="space-y-3 border-t border-slate-800 pt-4">
    <p className="text-[10px] font-bold text-slate-500 uppercase">
        Editing Field: <span className="text-blue-400">{selectedArea || 'None'}</span>
    </p>

    {/* Font Size */}
    <div className="flex items-center justify-between text-[10px]">
        <span className="text-slate-400 uppercase">Font Size</span>
        <select 
            className="bg-slate-800 border border-slate-700 text-white rounded px-2 py-1 outline-none"
            value={fieldStyles[selectedArea]?.fontSize || '10px'} 
            onChange={(e) => updateFieldStyle('fontSize', e.target.value)}
        >
            {['8px', '9px', '10px', '12px', '14px', '16px', '18px', '20px'].map(size => (
                <option key={size} value={size}>{size}</option>
            ))}
        </select>
    </div>

    {/* Font Weight */}
    <div className="flex items-center justify-between text-[10px]">
        <span className="text-slate-400 uppercase">Weight</span>
        <select
            className="bg-slate-800 border border-slate-700 text-white rounded px-2 py-1 outline-none"
            value={fieldStyles[selectedArea]?.fontWeight || '400'}
            onChange={(e) => updateFieldStyle('fontWeight', e.target.value)}
        >
            <option value="400">Normal</option>
            <option value="500">Medium</option>
            <option value="700">Bold</option>
            <option value="900">Black</option>
        </select>
    </div>

    {/* Font Color */}
    <div className="flex items-center justify-between text-[10px]">
        <span className="text-slate-400 uppercase">Color</span>
        <input
            type="color"
            value={fieldStyles[selectedArea]?.fontColor || '#000000'}
            onChange={(e) => updateFieldStyle('fontColor', e.target.value)}
            className="w-8 h-6 bg-transparent cursor-pointer border-none"
        />
    </div>
</div>

                    {/* Branding */}
                    {selectedArea === 'header' && (
                        <div className="space-y-2 border-t border-slate-800 pt-4 animate-pulse">
                            <p className="text-[10px] font-bold text-blue-400 uppercase">Header Branding</p>
                            <button onClick={() => fileInputRef.current.click()} className="w-full flex items-center gap-3 px-4 py-2 bg-white text-slate-900 rounded font-bold text-xs">
                                <Upload size={14} /> Replace Logo
                            </button>
                            <input type="file" ref={fileInputRef} className="hidden" onChange={(e) => {
                                const file = e.target.files[0];
                                const reader = new FileReader();
                                reader.onloadend = () => setCompanyInfo({ ...companyInfo, logo: reader.result });
                                reader.readAsDataURL(file);
                            }} />
                        </div>
                    )}

                    {/* Modification Tools */}
                    <div className="space-y-2 border-t border-slate-800 pt-4">
                        <p className="text-[10px] font-bold text-slate-500 uppercase">Structure Tools</p>
                        <button onClick={handleAddItem} className="w-full flex items-center gap-3 px-4 py-3 bg-emerald-600 hover:bg-emerald-500 rounded font-bold text-xs shadow-lg transition-all">
                            <Plus size={16} /> Add Row / Field
                        </button>
                        <button onClick={handleAddColumn} className="w-full flex items-center gap-3 px-4 py-3 bg-blue-600 hover:bg-blue-500 rounded font-bold text-xs shadow-lg transition-all">
                            <Columns size={16} /> Add Table Column
                        </button>
                        <button onClick={handleDeleteLast} className="w-full flex items-center gap-3 px-4 py-3 bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white rounded font-bold text-xs border border-red-900/50 transition-all">
                            <Trash2 size={16} /> Remove Last Entry
                        </button>
                    </div>


                </div>

                <div className="p-4 bg-slate-950 space-y-2">
                    <button onClick={() => setIsEditMode(!isEditMode)} className={`w-full py-3 rounded font-black text-xs uppercase tracking-widest ${isEditMode ? 'bg-slate-700' : 'bg-blue-600 animate-bounce'}`}>
                        {isEditMode ? "Preview Mode" : "Return to Edit"}
                    </button>
                    <button
                        onClick={exportReportPDF}
                        className="w-full bg-white text-slate-900 py-3 rounded font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-blue-50"
                    >
                        <Printer size={16} /> Export PDF Report
                    </button>

                </div>
            </aside>

            {/* --- MAIN CANVAS --- */}
            <main className="flex-1 overflow-auto p-12 flex justify-center items-start custom-scrollbar">

                <div
                    onClick={(e) => e.stopPropagation()}

                    className={`report-canvas bg-white shadow-2xl p-[10mm] border border-black relative transition-all duration-500 
        ${orientation === 'portrait' ? 'w-[210mm] min-h-[297mm]' : 'w-[297mm] min-h-[210mm]'} 
        ${!isEditMode ? 'print-mode' : ''}`}
                >

                    {/* 1. DYNAMIC HEADER - EXPANDED MIDDLE COLUMN */}
                    <header className="border-[2px] border-black flex h-24 mb-2">
                        {/* Left: Branding */}
                        <div className="w-1/4 border-r-[2px] border-black flex flex-col items-center justify-center p-1">
                            <input
                                onClick={() => setSelectedArea('companyName')}
                                style={{
                                    fontSize: fieldStyles.companyName.fontSize,
                                    fontWeight: fieldStyles.companyName.fontWeight,
                                    color: fieldStyles.companyName.fontColor,
                                }}
                                className={`w-full text-center bg-transparent border-none outline-none ${selectedArea === 'companyName' ? 'bg-blue-50' : ''}`}
                                value={companyInfo.name}
                                onChange={(e) => setCompanyInfo({ ...companyInfo, name: e.target.value })}
                            />
                            <input
                                onClick={() => setSelectedArea('tagline')}
                                style={{
                                    fontSize: fieldStyles.tagline.fontSize,
                                    fontWeight: fieldStyles.tagline.fontWeight,
                                    color: fieldStyles.tagline.fontColor,
                                }}
                                className={`w-full text-center bg-transparent mt-1 uppercase outline-none ${selectedArea === 'tagline' ? 'bg-blue-50' : ''}`}
                                value={companyInfo.tagline}
                                onChange={(e) => setCompanyInfo({ ...companyInfo, tagline: e.target.value })}
                            />
                        </div>

                        {/* Middle: Title */}
                        <div className="w-1/2 border-r-[2px] border-black flex items-center justify-center p-2">
                            <textarea
                                onClick={() => setSelectedArea('reportTitle')}
                                style={{
                                    fontSize: fieldStyles.reportTitle.fontSize,
                                    fontWeight: fieldStyles.reportTitle.fontWeight,
                                    color: fieldStyles.reportTitle.fontColor,
                                }}
                                className={`text-center w-full bg-transparent resize-none outline-none ${selectedArea === 'reportTitle' ? 'bg-blue-50' : ''}`}
                                defaultValue={"PHASED ARRAY ULTRASONIC\nINSPECTION REPORT"}
                            />
                        </div>

                        {/* Right: Metadata */}
                        <div className="w-1/4 flex flex-col">
                            {/* No Field */}
                            <div className="flex-1 px-1 border-b-[2px] border-black flex items-center justify-between">
                                <span className="text-[8px] text-slate-400">No:</span>
                                <input
                                    onClick={() => setSelectedArea('metaNo')}
                                    style={{
                                        fontSize: fieldStyles.metaNo.fontSize,
                                        fontWeight: fieldStyles.metaNo.fontWeight,
                                        color: fieldStyles.metaNo.fontColor,
                                    }}
                                    className="text-right bg-transparent outline-none w-20"
                                    defaultValue="REP-001"
                                />
                            </div>
                            {/* Date Field */}
                            <div className="flex-1 px-1 border-b-[2px] border-black flex items-center justify-between">
                                <span className="text-[8px] text-slate-400">Date:</span>
                                <input
                                    onClick={() => setSelectedArea('metaDate')}
                                    style={{
                                        fontSize: fieldStyles.metaDate.fontSize,
                                        fontWeight: fieldStyles.metaDate.fontWeight,
                                        color: fieldStyles.metaDate.fontColor,
                                    }}
                                    className="text-right bg-transparent outline-none w-20"
                                    defaultValue="05/01/2026"
                                />
                            </div>
                        </div>
                    </header>

                    {/* 1. GENERAL DETAILS TABLE */}
                    <div
                        onClick={() => setSelectedArea('general')}
                        className={`relative mb-4 ${getSelectionStyle('general')}`}
                    >

                        <div className="bg-slate-900 text-white text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-t-md">
                            General Details
                        </div>

                        <div className="grid grid-cols-3 border-t-[2px] border-l-[2px] border-black">

                            {Object.entries(reportGeneralDetails).map(([key, val], i) => (
                                <div key={i} className="border-r-[2px] border-b-[2px] border-black p-1.5 h-11 flex flex-col justify-center bg-white">
                                    <span className="block text-[8.5px] font-black uppercase text-slate-600 leading-none mb-1 ">
                                        {key}
                                    </span>
                                    <input
                                        onClick={() => setSelectedArea('general')} // Select the group
                                        style={{
                                            fontSize: fieldStyles.general.fontSize,
                                            fontWeight: fieldStyles.general.fontWeight,
                                            color: fieldStyles.general.fontColor,
                                        }}
                                        className="w-full bg-transparent border-none p-0 focus:ring-0 leading-tight"
                                        value={val}
                                        onChange={(e) => setReportGeneralDetails(prev => ({ ...prev, [key]: e.target.value }))}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>


                    {/* 2. MATERIAL TABLE */}
                    {/* MATERIAL DETAILS SECTION */}
                    <div
                        onClick={() => setSelectedArea('part')}
                        className={`relative mb-4 ${getSelectionStyle('part')}`}
                    >
                        {/* TITLE BAR */}
                        <div className="bg-slate-900 text-white text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-t-md">
                            Inspection Setup Details
                        </div>

                        {/* TABLE */}
                        <div className="grid grid-cols-3 border-t-[2px] border-l-[2px] border-black">
                            {Object.entries(reportPartDetails).map(([key, val], i) => (
                                <div
                                    key={i}
                                    className="border-r-[2px] border-b-[2px] border-black p-1.5 h-9 flex flex-col justify-center bg-white"
                                >
                                    {/* Field Label - Non-customizable or linked to a 'label' style if desired */}
                                    <input
                                        className="block text-[8.5px] font-black uppercase text-slate-600 w-full bg-transparent leading-none mb-0.5"
                                        value={key}
                                        readOnly
                                    />

                                    {/* Value - Now fully dynamic based on fieldStyles.part */}
                                    <input
                                        onClick={() => setSelectedArea('part')}
                                        style={{
                                            fontSize: fieldStyles.part.fontSize,
                                            fontWeight: fieldStyles.part.fontWeight,
                                            color: fieldStyles.part.fontColor,
                                        }}
                                        className={`w-full bg-transparent border-none p-0 focus:ring-0 leading-tight transition-colors ${selectedArea === 'part' ? 'bg-blue-50/50' : ''
                                            }`}
                                        value={val}
                                        onChange={(e) => {
                                            const next = { ...reportPartDetails };
                                            next[key] = e.target.value;
                                            setReportPartDetails(next);
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>


                    {/* 3. EQUIPMENT TABLE */}

                    <div
                        onClick={() => setSelectedArea('scan')}
                        className={`relative mb-4 ${getSelectionStyle('scan')}`}
                    >
                        <div className="bg-slate-900 text-white text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-t-md">
                            Data Acquisition Parameters
                        </div>
                        <div className="grid grid-cols-3 border-t-[2px] border-l-[2px] border-black">

                            {Object.entries(reportScanParams).map(([key, val], i) => (
                                /* Added onClick to the container for better UX */
                                <div
                                    key={i}
                                    onClick={() => setSelectedArea('scan')}
                                    className={`border-r-[2px] border-b-[2px] border-black p-1 flex flex-col justify-center min-h-[28px] transition-colors ${selectedArea === 'scan' ? 'bg-blue-50/50' : 'bg-white'
                                        }`}
                                >
                                    <input
                                        className="block text-[8.5px] font-black uppercase text-slate-600 w-full bg-transparent leading-none mb-0.5"
                                        value={key}
                                        readOnly
                                    />
                                    <input
                                        /* Apply dynamic styles from the scan configuration */
                                        style={{
                                            fontSize: fieldStyles.scan.fontSize,
                                            fontWeight: fieldStyles.scan.fontWeight,
                                            color: fieldStyles.scan.fontColor,
                                        }}
                                        className="w-full bg-transparent border-none p-0 focus:ring-0 leading-tight outline-none"
                                        value={val}
                                        onChange={(e) => {
                                            const next = { ...reportScanParams };
                                            next[key] = e.target.value;
                                            setReportScanParams(next);
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 4. MAIN FINDINGS TABLE */}
                    <div
                        onClick={() => setSelectedArea('table')}
                        className={`border-t-[2px] border-l-[2px] border-black mb-4 overflow-hidden transition-all ${getSelectionStyle('table')}`}
                    >
                        <table className="w-full border-collapse table-fixed bg-white">
                            <thead className="bg-slate-50 font-black uppercase italic">
                                <tr>
                                    <th className="border-r-[2px] border-b-[2px] border-black w-6 p-0.5 pb-1 align-middle text-[7px]">Sr</th>
                                    {Object.keys(reportIndications[0] || {})
                                        .filter((k) => k !== 'id')
                                        .map((col, i) => (
                                            <th key={i} className="border-r-[2px] border-b-[2px] border-black p-0.5 leading-tight break-words">
                                                <textarea
                                                    rows={1}
                                                    style={{
                                                        // Headers usually stay small, but we can link weight/color
                                                        color: fieldStyles.table.fontColor,
                                                        fontWeight: '900'
                                                    }}
                                                    className="w-full text-center bg-transparent resize-none overflow-hidden border-none focus:ring-0 p-0 pb-1 -mb-1 text-[7px]"
                                                    defaultValue={columnMapping[col] || col.replace('_', ' ')}
                                                />
                                            </th>
                                        ))}
                                </tr>
                            </thead>
                            <tbody>
                                {reportIndications.map((row, idx) => (
                                    <tr key={idx} className="h-6">
                                        <td className="border-r-[2px] border-b-[2px] border-black text-center font-bold text-[8px] bg-slate-50/30 pb-1.5">
                                            {idx + 1}
                                        </td>
                                        {Object.keys(row)
                                            .filter((k) => k !== 'id')
                                            .map((col, i) => (
                                                <td key={i} className="border-r-[2px] border-b-[2px] border-black p-0">
                                                    <input
                                                        /* Apply dynamic styles from the table configuration */
                                                        style={{
                                                            fontSize: fieldStyles.table.fontSize,
                                                            fontWeight: fieldStyles.table.fontWeight,
                                                            color: fieldStyles.table.fontColor,
                                                        }}
                                                        className="w-full text-center bg-transparent border-none focus:bg-yellow-50 px-0.5 pb-1.5 pt-0 -mt-0.5 outline-none"
                                                        value={
                                                            col === 'status' || col === 'reject_reason'
                                                                ? row[col]
                                                                : formatValue(row[col])
                                                        }
                                                        onChange={(e) => {
                                                            const updated = [...reportIndications];
                                                            updated[idx][col] = e.target.value;
                                                            setReportIndications(updated);
                                                        }}
                                                    />
                                                </td>
                                            ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* 5. SIGNATURE FOOTER */}
                    <footer className="mt-auto flex justify-between pt-10 px-8 text-center uppercase">
                        <div className="w-1/3 border-t-2 border-black py-2"><p className="font-black text-[10px]">Inspected By</p><p className="text-[7px]">ASNT Level II</p></div>
                        <div className="w-1/3 border-t-2 border-black py-2 ml-10"><p className="font-black text-[10px]">Reviewed By</p><p className="text-[7px]">Client Rep</p></div>
                    </footer>


                    {location.state?.weldImage && (
                        <div className="break-before-page pt-8 flex flex-col items-center justify-center w-full">
                            {/* Optional Title for the Image Section */}
                            <div className="w-full mb-4">
                                <div className="border-t-[2px] border-b-[2px] border-black py-1.5 text-center">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-black">
                                        Visual Evidence – Weld Scan Image
                                    </span>
                                </div>
                            </div>

                            <div className="border-[2px] border-black p-2 bg-white shadow-sm max-w-[90%]">
                                <img
                                    src={location.state.weldImage}
                                    alt="Weld Inspection"
                                    className="max-h-[600px] w-auto object-contain"
                                />
                            </div>

                            <p className="mt-2 text-[8px] font-bold text-slate-500 uppercase italic">
                                Attachment: Detailed Scan Result
                            </p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default LiveReportPreview;